import {
  Box,
  Button,
  Flex,
  MenuItem,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import MuiIcon from 'components/MuiIcon';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  useActivateOneUserResource, useDeactivateOneUserResource,
  useDeleteUserResource, useGetOneUserResource
} from 'shared/api/requests/user-resources';
import Card from 'shared/components/horizon-ui/card/Card';
import Link from 'shared/components/Link';
import Loader from 'shared/components/Loader';
import MainItemMenu from 'shared/components/MainItemMenu';
import MainModelInfo from 'shared/components/MainModelInfo';
import useGetColors from 'shared/hooks/useGetColors';
import { PathsE, PathsParams } from 'shared/routes';
import DeleteModal from '../admin/DeleteModal';
import PageTitle from '../admin/PageTitle';
import UserResourceInfo from './ModelInfo';

const ViewUserResource = () => {
  const { t } = useTranslation();
  const { resourceId } = useParams<PathsParams[PathsE.SHOW_USER_RESOURCE]>();
  const {
    gray800,
    gray700,
    gray100,
  } = useGetColors();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const requestParams = {
    pathParams: { resourceId },
    params: {},
  };
  const { mutate: activateMutation } = useActivateOneUserResource();
  const { mutate: deactivateMutation } = useDeactivateOneUserResource();
  const handleActivate = (id: string) => activateMutation({ id });
  const handleDeactivate = (id: string) => deactivateMutation({ id });

  const { mutate: deleteMutate } = useDeleteUserResource(requestParams);
  const { data, isLoading } = useGetOneUserResource(requestParams);

  const handleDelete = () => onOpen();

  const renderName = () => t('users.usersResources');

  const breadcrumbs = [
    {
      path: PathsE.HOME,
      text: t('general.home'),
    },
    {
      path: PathsE.USER_RESOURCES,
      text: t('users.users'),
    },
  ];

  if (isLoading || !data) return <Loader />;

  return (
    <>
      <Box>
        <PageTitle
          title={t('users.usersResources')}
          breadcrumbs={breadcrumbs}
          backUrl={{
            path: PathsE.USER_RESOURCES,
          }}
        />
        <Card mb="20px" borderRadius="20px" px="30px">
          <Flex justifyContent="space-between" alignItems="center" mt="-10px">
            <Text color={gray700} fontWeight="600">
              {renderName()}
            </Text>
            <Flex alignItems="center">
              <Box mr="10px">
                <Link<PathsParams[PathsE.UPDATE_USER_RESOURCE]>
                  path={PathsE.UPDATE_USER_RESOURCE}
                  pathsParams={{ resourceId }}
                >
                  <Button
                    color={gray800}
                    bgColor={gray100}
                    borderRadius="8px"
                    leftIcon={<MuiIcon name="edit" />}
                  >
                    {t('users.editUserResource')}
                  </Button>
                </Link>
              </Box>
              <MainItemMenu
                isModelView
                data={data}
                handleActivate={handleActivate}
                handleDeactivate={handleDeactivate}
                viewLink={PathsE.UPDATE_USER_RESOURCE}
                pathsParams={{ resourceId }}
              >
                <MenuItem
                  _hover={{
                    backgroundColor: gray100,
                  }}
                  onClick={handleDelete}
                >
                  <MuiIcon name="delete" />
                  <Text color={gray700} ml="8px" fontSize="sm">{t('users.deleteUserResource')}</Text>
                </MenuItem>
              </MainItemMenu>
            </Flex>
          </Flex>
          <MainModelInfo data={data} />
          <UserResourceInfo data={data} />
        </Card>
      </Box>
      <DeleteModal
        title={t('users.deleteUserResource')}
        text={t('general.approveTo', { phrase: t('users.deleteUserResource') })}
        onClose={onClose}
        isOpen={isOpen}
        onSubmit={() => deleteMutate({})}
      />
    </>
  );
};

export default ViewUserResource;
